import React, { useState } from "react";
import { Bar } from "react-chartjs-2";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Tooltip,
  Legend,
} from "chart.js";
import ChartDataLabels from "chartjs-plugin-datalabels";
import { ChevronDownButton } from "~misc/icons";
import Info from "~components/Form/components/Info";

ChartJS.register(CategoryScale, LinearScale, BarElement, Tooltip, Legend, ChartDataLabels);

const seriesColors = {
  mapeado: "#034EA2",
  negociado: "#FF8A00",
  faturado: "#9ACA3C",
};

const legendItems = [
  { key: "mapeado", label: "Volume Mapeado", color: seriesColors.mapeado },
  { key: "negociado", label: "Volume Negociado", color: seriesColors.negociado },
  { key: "faturado", label: "Volume Faturado", color: seriesColors.faturado },
];

const buildDatasets = (data) =>
  legendItems.map(({ key, label, color }) => ({
    label,
    data: data.map((d) => d[key] ?? null),
    backgroundColor: color,
    borderRadius: 2,
    categoryPercentage: 0.7,
    barPercentage: 0.9,
  }));

const chartOptions = {
  responsive: true,
  maintainAspectRatio: false,
  plugins: {
    legend: { display: false },
    tooltip: { enabled: true },
    datalabels: {
      anchor: "end",
      align: "top",
      color: "#01054C",
      font: { size: 13, weight: "bold" },
      formatter: (value) => (value != null ? value : ""),
    },
  },
  scales: {
    x: {
      grid: { display: false },
      ticks: { font: { size: 10 }, color: "#555" },
    },
    y: {
      display: false,
      grid: { display: false },
      grace: "15%",
    },
  },
  layout: { padding: { top: 28 } },
};

const MappedVsConsumed = ({ data = [] }) => {
  const [open, setOpen] = useState(true);
  const [hidden, setHidden] = useState({});

  const toggleSeries = (key) => {
    setHidden((prev) => ({ ...prev, [key]: !prev[key] }));
  };

  const chartData = {
    labels: data.map((d) => d.label),
    datasets: buildDatasets(data).map((ds, i) => {
      const key = legendItems[i].key;
      return hidden[key]
        ? { ...ds, data: ds.data.map(() => null), backgroundColor: "transparent" }
        : ds;
    }),
  };

  return (
    <div className="rounded-xl overflow-hidden shadow">
      <div className="bg-pendente flex items-center justify-center relative px-4 py-3">
        <span className="text-white font-bold text-sm">Mapeado vs Consumido</span>
        <button
          type="button"
          onClick={() => setOpen((prev) => !prev)}
          className="absolute right-4 top-1/2 -translate-y-1/2"
        >
          <div
            style={{
              transform: open ? "rotate(0deg)" : "rotate(180deg)",
              transition: "transform 0.25s ease",
            }}
          >
            <ChevronDownButton className="w-9 h-9" />
          </div>
        </button>
      </div>

      {open && (
        <div className="bg-white px-4 pt-4 pb-3">
          <div style={{ height: 220 }}>
            <Bar data={chartData} options={chartOptions} />
          </div>
          <div className="flex flex-wrap gap-x-6 gap-y-1 mt-3">
            {legendItems.map(({ key, label, color }) => (
              <div key={key} className="flex items-center gap-1.5">
                <button
                  type="button"
                  onClick={() => toggleSeries(key)}
                  className="flex items-center gap-1.5"
                  style={{ opacity: hidden[key] ? 0.35 : 1 }}
                >
                  <span
                    className="inline-block w-3 h-3 rounded-full flex-shrink-0"
                    style={{ backgroundColor: color }}
                  />
                  <span className="text-xs text-gray-600">{label}</span>
                </button>
                {key === 'faturado' && (
                  <Info
                    bg="blue"
                    position="mobile-left"
                    text="Considera somatória dos volumes dos últimos 6M"
                    tooltipClass="w-6 h-6 relative"
                  />
                )}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default MappedVsConsumed;
